import { Breadcrumb } from 'antd';
import { useLayoutEffect } from 'react';
import { Link, useLocation, useMatches } from 'react-router-dom';

import {
  appRouteHandle,
  createRestoreNavigationState,
  resolveReturnTarget,
  scrollRestorationKey,
} from './route-navigation.js';

export function AppBreadcrumb() {
  const location = useLocation();
  const matches = useMatches();
  const currentHandle = [...matches]
    .reverse()
    .map((match) => appRouteHandle(match.handle))
    .find((handle) => handle?.breadcrumb !== undefined);
  if (!currentHandle?.breadcrumb) return null;

  const returnTarget = currentHandle.listPath
    ? resolveReturnTarget(location.state, currentHandle.listPath)
    : undefined;
  return (
    <Breadcrumb
      aria-label="页面路径"
      className="app-breadcrumb"
      items={[
        ...(returnTarget
          ? [
              {
                title: (
                  <Link
                    state={createRestoreNavigationState(returnTarget.scrollKey)}
                    to={returnTarget.path}
                  >
                    {returnTarget.label}
                  </Link>
                ),
              },
            ]
          : []),
        { title: currentHandle.breadcrumb },
      ]}
    />
  );
}

export function AppScrollRestoration() {
  const location = useLocation();
  const key = scrollRestorationKey(location);

  useLayoutEffect(() => {
    const container = document.querySelector<HTMLElement>('.app-content');
    if (!container) return;
    const scrollContainer = container;

    const target = readScrollPosition(key);
    let restoreFrame = 0;
    let restoreTimer = 0;
    const observer = new MutationObserver(restore);
    function stopRestoring() {
      observer.disconnect();
      window.clearTimeout(restoreTimer);
      window.cancelAnimationFrame(restoreFrame);
    }
    function restore() {
      const maxScrollTop = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
      scrollContainer.scrollTop = Math.min(target, maxScrollTop);
      if (maxScrollTop >= target) stopRestoring();
    }
    function save() {
      writeScrollPosition(key, scrollContainer.scrollTop);
    }

    scrollContainer.addEventListener('scroll', save, { passive: true });
    observer.observe(scrollContainer, { childList: true, subtree: true });
    restoreFrame = window.requestAnimationFrame(restore);
    restoreTimer = window.setTimeout(stopRestoring, 2_000);

    return () => {
      stopRestoring();
      scrollContainer.removeEventListener('scroll', save);
    };
  }, [key]);

  return null;
}

const SCROLL_STORAGE_KEY = 'badminton-scroll-positions';

function readScrollPosition(key: string): number {
  try {
    const positions = JSON.parse(
      window.sessionStorage.getItem(SCROLL_STORAGE_KEY) ?? '{}',
    ) as Record<string, unknown>;
    const position = positions[key];
    return typeof position === 'number' && Number.isFinite(position) && position >= 0
      ? position
      : 0;
  } catch {
    return 0;
  }
}

function writeScrollPosition(key: string, position: number): void {
  try {
    const positions = JSON.parse(
      window.sessionStorage.getItem(SCROLL_STORAGE_KEY) ?? '{}',
    ) as Record<string, unknown>;
    positions[key] = position;
    window.sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Session storage can be unavailable in hardened browser contexts.
  }
}
